import {
    gsap,
    Power2
} from 'gsap'
import {
    debounce,
    throttle
} from 'throttle-debounce'

export default class Player {
    constructor(_container) {
        this.wrapper = _container
        this.container = _container.querySelector("figure")
        this.video = this.wrapper.querySelector("video")
        this.video.volume = 0
        this.playing = false
        // buttons
        this.progressContainer = this.wrapper.querySelector(".progress")
        this.progressBar = this.wrapper.querySelector(".progress-bar")
        this.timer = this.wrapper.querySelector(".timer")
        this.togglePlayBtn = this.wrapper.querySelector(".toggle-play-btn")
        this.manageSeek = false

        // audio
        this.toggleVolumeBtn = this.wrapper.querySelector(".toggle-volume-btn")
        this.rewindBtn = this.wrapper.querySelector(".rewind-btn")
        this.forwardBtn = this.wrapper.querySelector(".forward-btn")
        this.events()
        this.modalTimeline()
    }

    seek(_event) {
        this.manageSeek = _event
    }

    get duration() {
        return this.video.duration
    }

    seekTo(e, _moving = false) {
        if (e.touches) e = e.touches[0]
        if (!this.manageSeek && _moving) return false
        let width = this.progressBar.getBoundingClientRect().width
        let currentTime = (e.offsetX * this.duration) / width
        this.video.currentTime = currentTime
        this.render()
    }

    closePlayer() {
        if (this.modalTimeline) {
            this.playerContainer.classList.remove("open")
            this.modalTl.duration(.85).reverse()
            this.pause()
        }
    }

    events() {
        let closeBtn = document.querySelector("#close-video")
        window.addEventListener("keyup", (e) => {
            if(String(e.key).toLowerCase() == "escape" && this.playing) return this.closePlayer()
        })
        if (closeBtn) {
            closeBtn.addEventListener("click", () => this.closePlayer(), false)
        }
        // seek
        this.progressContainer.addEventListener("pointerdown", () => this.seek(true), {
            passive: true
        })
        this.progressContainer.addEventListener("pointerup", () => this.seek(false), {
            passive: true
        })
        this.progressBar.addEventListener("click", (e) => this.seekTo(e))
        this.progressBar.addEventListener("pointermove", (e) => this.seekTo(e, true), {
            passive: true
        })
        // show videostate 
        if (!this.isMobile()) {
            this.showState = throttle(30, (e) => {
                this.wrapper.classList.add("visible")
            })
            this.hideState = debounce(1000, (e) => {
                this.wrapper.classList.remove("visible")
            })
            this.video.addEventListener("pointermove", (e) => this.showState(), {
                passive: true
            })
            this.video.addEventListener("pointermove", (e) => this.hideState(), {
                passive: true
            })

            this.video.addEventListener("mouseleave", () => {
                this.wrapper.classList.remove("visible")
            })
        }

        this.video.addEventListener("click", () => {
            return this.togglePlay()
        })
        this.togglePlayBtn.addEventListener("click", () => this.togglePlay())
        this.video.addEventListener("ended", () => this.reset())

        document.querySelectorAll(".play-video").forEach(el => el.addEventListener("click", (e) => this.onLinkClick(e, el), false))
    }

    onLinkClick(e, el) {
        e.preventDefault()
        let video = document.querySelector("#video")
        video.src = el.dataset.video
        let videoTitle = document.querySelector("#video-title")
        videoTitle.innerText = el.getAttribute("title")
        this.modalTl.duration(1.65).play()
        this.playerContainer.classList.add("open")
    }

    modalTimeline() {
        this.playerContainer = document.querySelector("#player-container")
        if (!this.playerContainer) return false
        this.modalTl = gsap.timeline({
            paused: true
        }).to(this.playerContainer, {
            duration: .45,
            display: "block",
            opacity: 1,
            onComplete: () => {
                this.resize()
            }
        }, 0).to("#app", {
            ease: Power2.easeOut,
            duration: .85,
            opacity: 0
        }, 0).to(this.playerContainer.querySelectorAll(".intro-item"), {
            duration: 1,
            stagger: 0.125,
            clipPath: "inset(0% 0% 0% 0%)",
            ease: Power2.easeOut,
            force3D: true,
            y: "0%",
            opacity: 1
        }, .35).to("#video-container figure", {
            duration: .45,
            opacity: 1,
            onComplete: () => {
                this.play()
            }
        }, .45).to("#player-container footer", {
            duration: .65,
            opacity: 1
        }, .85)
    }

    isMobile() {
        return MOBILE
    }

    togglePlay() {
        this.togglePlayBtn.blur()
        if (this.playing) return this.pause()
        else this.play()
    }

    animate() {
        if (this.playing) requestAnimationFrame(() => this.animate())
        this.render()
    }

    resize() {
        let video = document.querySelector("#video-container")
        if (video) {
            let _width = video.getBoundingClientRect().width
            video.style.height = `${~~(_width * .4375)}px`
        }
    }

    render() {
        if (!this.video) return false
        const current = this.video.currentTime
        const progress = (current * 100) / this.duration
        this.progressBar.value = progress.toFixed(1)
        this.updateTimer(progress)
    }

    updateTimer(_progress) {
        this.timer.innerText = this.timeFormatter(this.video.currentTime)
        let width = this.progressBar.getBoundingClientRect().width
        let _x = (this.video.currentTime * width) / this.duration
        this.timer.style.transform = `translateX(${((_progress*width)/100)}px)`
    }

    timeFormatter(_time) {
        let minutes = ~~((_time % (60 * 60)) / 60)
        if (String(minutes).length == 1) minutes = `0${minutes}`
        let seconds = ~~(_time % 60)
        if (String(seconds).length == 1) seconds = `0${seconds}`
        return `${minutes}:${seconds}`
    }

    play() {
        if (this.playing) return false
        this.togglePlayBtn.classList.add('playing')
        this.wrapper.classList.remove('visible')
        this.playing = true
        this.video.play()
        this.volumeTo(1, () => {
            this.wrapper.classList.add("bg-pause")
        })
        this.animate()
    }

    reset() {
        this.pause()
        this.video.currentTime = 0
    }

    pause() {
        if (!this.playing) return false
        this.wrapper.classList.remove('visible')
        this.playing = false
        this.volumeTo(0, () => {
            this.togglePlayBtn.classList.remove('playing')
            this.wrapper.classList.remove("bg-pause")
            if (!this.playing && this.video) this.video.pause()
        })
    }

    volumeTo(val, callback) {
        gsap.to(this.video, {
            ease: Power2.easeOut,
            duration: .35,
            volume: val,
            onComplete: () => {
                if (callback) callback()
            }
        })
    }

}